import React from "react";
import Button from "@surgia/ui/components/button";
import VisibleIcon from "@surgia/ui/icons/visible/visible.svg";
import InvisibleIcon from "@surgia/ui/icons/visible/invisible.svg";
import Badge from "@surgia/ui/components/badge";
import { useCustomerView } from "../hooks/useCustomerView";
import ProductIcon from "@surgia/ui/icons/catalog/parentProduct.svg";
import VariantIcon from "@surgia/ui/icons/catalog/variantProduct.svg";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { productQueries } from "@surgia/api/src/queryStore/admin/product";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
export interface ProductConfig {
  id: string;
  name: string;
  price: number | null;
  minQuantity: number | null;
  maxQuantity: number | null;
  customerProductId: string;
  customerProductName?: string;
  isParent: boolean;
  tenantProductId: string;
  variants?: ProductConfig[];
  productId?: number;
  isVisible?: boolean;
  tenantInfoId?: string;
  hasInitialValues?: boolean;
  hasChanges?: boolean;
  tenantInfo: any;
}

interface ProductCard2Props {
  product: any;
  expandedProducts: Set<string>;
  expandedVariants: Set<string>;
  productConfigs: Record<string, ProductConfig>;
  updateProductConfig: (
    productId: string,
    field: keyof ProductConfig,
    value: any
  ) => void;
  isProductConfigValid: (productId: string) => boolean;
  handleSaveProduct: (
    productId: string,
    tenantProductId?: string,
    formData?: any
  ) => Promise<void>;
  toggleProductExpansion: (productId: string) => void;
  toggleVariantExpansion: (variantId: string) => void;
  isUpdatingProduct: boolean;
  updatingProductId?: string | null; // Add this new prop
  updateCustomerViewMutation: any;
  productDetailsPage?: boolean;
  selectedVariants?: Set<string>;
  onAssignParentProduct?: (productId: string) => Promise<void>;
  isAssigningParentProduct?: boolean;
  // Optional variations data to avoid duplicate fetching
  variations?: any[];
  showOnlyCurrentlyAssignedVariants?: boolean;
  currencyCode?: string; // Currency code from tenant's storefront
}

// Memoized VariantCard component
const VariantCard = React.memo(
  ({
    variant,
    product,
    productConfigs,
    updateProductConfig,
    handleSaveProduct,
    toggleVariantExpansion,
    isUpdatingProduct,
    updatingProductId, // Add this new prop
    expandedVariants,
    productDetailsPage,
    showOnlyCurrentlyAssignedVariants,
    tenantId,
  }: {
    variant: any;
    product: any;
    productConfigs: Record<string, ProductConfig>;
    updateProductConfig: (
      productId: string,
      field: keyof ProductConfig,
      value: any
    ) => void;
    isProductConfigValid: (productId: string) => boolean;
    handleSaveProduct: (
      productId: string,
      tenantProductId?: string,
      formData?: any
    ) => Promise<void>;
    toggleVariantExpansion: (variantId: string) => void;
    isUpdatingProduct: boolean;
    updatingProductId?: string | null; // Add this new prop
    expandedVariants: Set<string>;
    productDetailsPage?: boolean;
    showOnlyCurrentlyAssignedVariants?: boolean;
    tenantId: string;
  }) => {
    const isVariantExpanded = expandedVariants.has(variant.id);
    const variantConfig = productConfigs[variant.id] || variant;
    // Check if this specific variant is being updated
    const isVariantSaving =
      isUpdatingProduct && updatingProductId === variant.tenantProductId;
    const queryClient = useQueryClient();

    // React Hook Form for variant
    const {
      register,
      formState: { isDirty, errors },
      reset,
      watch,
    } = useForm({
      defaultValues: {
        price: variantConfig?.price ?? null,
        minQuantity: variantConfig?.minQuantity ?? null,
        maxQuantity: variantConfig?.maxQuantity ?? null,
        customerProductId: variantConfig?.customerProductId || "",
      },
      mode: "onChange",
    });

    // Watch current form values
    const currentFormValues = watch();
    // Use the custom hook for customer view
    const {
      customerView: variantCustomerView,
      toggleCustomerView: toggleVariantCustomerView,
      isDisabled: isVariantCustomerViewDisabled,
      isLoading: isVariantCustomerViewLoading,
    } = useCustomerView({
      initialCustomerView: variant.customer_view || false,
      productId: variant.id.toString(),
      isDisabled:
        variant?.price === null ||
        variant?.maxQuantity === null ||
        variant?.minQuantity === null,
      tenantId: tenantId || "",
      parentProductId: product?.id?.toString(),
    });
    // Check if parent product is visible
    if (showOnlyCurrentlyAssignedVariants && !variant?.isVisible) {
      return null;
    }
    // Handle variant customer view toggle with parent visibility check
    const handleVariantCustomerViewToggle = () => {
      if (product.customer_view === false) {
        toast.info(
          "To change variant visibility, the parent product must be visible first."
        );
        return;
      }

      // If parent is visible, allow the toggle
      toggleVariantCustomerView();
    };

    // Format feature values for display
    const featureDisplay =
      variant?.catalog_info?.feature_values
        ?.map((feature: any) => feature.featureVariant.variant_name)
        .join(", ") || "";
    return (
      <div
        className="bg-gray-50 rounded-lg border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Variant Header */}
        <div
          className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-100"
          onClick={() => toggleVariantExpansion(variant.id)}
        >
          <div className="flex items-center gap-3">
            {/* <img src={VariantIcon} alt="variant" /> */}
            <img
              src={variant.image ?? VariantIcon}
              alt="variant"
              className="rounded-full w-[40px] h-[40px]"
            />

            <div>
              <h4 className="text-gray-900 font-medium text-sm">
                {product.name}
              </h4>
              <p className="text-xs text-gray-500">{featureDisplay}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Status Badge */}
            {!productDetailsPage && (
              <Badge
                variant={isVariantCustomerViewDisabled ? "default" : "active"}
                size="sm"
                className="!rounded-lg"
              >
                {isVariantCustomerViewDisabled ? "Draft" : "Completed"}
              </Badge>
            )}
            {productDetailsPage && (
              <Badge
                variant={!variantCustomerView ? "default" : "active"}
                size="sm"
                className="!rounded-lg"
              >
                {!variantCustomerView ? "Draft" : "Published"}
              </Badge>
            )}
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform ${isVariantExpanded ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {/* Variant Expanded Content */}
        {isVariantExpanded && (
          <div className="px-4 py-4 border-t border-gray-200 space-y-4">
            {/* Variant Configuration Fields */}
            <div className="space-y-4">
              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price <span className="text-primary-blue">*</span>
                </label>
                <input
                  {...register("price", {
                    required: "Price is required",
                    min: { value: 0, message: "Price must be positive" },
                  })}
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This is the recommended price after calculating the user
                  account discount.
                </p>
              </div>

              {/* Quantity Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Quantity{" "}
                    <span className="text-primary-blue">*</span>
                  </label>
                  <input
                    {...register("minQuantity", {
                      required: "Minimum quantity is required",
                      min: {
                        value: 1,
                        message: "Minimum quantity must be at least 1",
                      },
                    })}
                    type="number"
                    placeholder="Minimum Quantity"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Quantity{" "}
                    <span className="text-primary-blue">*</span>
                  </label>
                  <input
                    {...register("maxQuantity", {
                      required: "Maximum quantity is required",
                      min: {
                        value: 1,
                        message: "Maximum quantity must be at least 1",
                      },
                      max: {
                        value: 999,
                        message: "Maximum quantity cannot exceed 999",
                      },
                    })}
                    type="number"
                    placeholder="Maximum Quantity"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
                  />
                  {errors.maxQuantity?.message ==
                    "Maximum quantity cannot exceed 999" && (
                    <p className="text-red-500 text-xs ">
                      {errors.maxQuantity?.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Customer Product ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer - Product ID{" "}
                </label>
                <input
                  {...register("customerProductId", {
                    pattern: {
                      value: /^[a-zA-Z0-9]+$/,
                      message:
                        "Customer Product ID can only contain letters and numbers",
                    },
                    minLength: {
                      value: 2,
                      message:
                        "Customer Product ID must be at least 2 characters",
                    },
                    maxLength: {
                      value: 50,
                      message:
                        "Customer Product ID must be less than 50 characters",
                    },
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
                />
                {errors.customerProductId && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.customerProductId.message}
                  </p>
                )}
              </div>

              {/* Save Button for Variant */}
              <div className="flex justify-between pt-2">
                {/* Customer View Toggle */}
                <div
                  className={`flex items-center gap-3 cursor-pointer ${
                    isVariantCustomerViewDisabled
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                  onClick={handleVariantCustomerViewToggle}
                >
                  <img
                    src={variantCustomerView ? VisibleIcon : InvisibleIcon}
                    alt="visibility toggle"
                    className="w-11 h-6"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {isVariantCustomerViewLoading ? "Updating..." : "Visible"}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      reset();
                    }}
                    disabled={isVariantSaving || !isDirty} // Disable when saving or no changes
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={async () => {
                      const formData = {
                        price: currentFormValues.price || null,
                        minQuantity: currentFormValues.minQuantity || null,
                        maxQuantity: currentFormValues.maxQuantity || null,
                        customerProductId:
                          currentFormValues.customerProductId || "",
                      };

                      try {
                        await handleSaveProduct(
                          variant.id,
                          variant.id.toString(),
                          formData
                        );

                        // Update productConfigs with new values after successful save
                        updateProductConfig(
                          variant.id,
                          "price",
                          formData.price
                        );
                        updateProductConfig(
                          variant.id,
                          "minQuantity",
                          formData.minQuantity
                        );
                        updateProductConfig(
                          variant.id,
                          "maxQuantity",
                          formData.maxQuantity
                        );
                        updateProductConfig(
                          variant.id,
                          "customerProductId",
                          formData.customerProductId
                        );

                        // Mark as having initial values
                        updateProductConfig(
                          variant.id,
                          "hasInitialValues",
                          true
                        );
                        updateProductConfig(variant.id, "hasChanges", false);

                        // Reset form with the saved values as new defaults
                        reset(formData);
                      } catch (error) {
                        console.error("Error saving variant:", error);
                      }

                      queryClient.invalidateQueries({
                        queryKey: ["shared", "tenant-products"],
                      });
                      queryClient.invalidateQueries({
                        queryKey: ["shared", "common-products"],
                      });
                    }}
                    disabled={
                      !isDirty ||
                      isVariantSaving ||
                      Object.keys(errors).length > 0 ||
                      currentFormValues.minQuantity === null ||
                      currentFormValues.maxQuantity === null ||
                      currentFormValues.minQuantity === undefined ||
                      currentFormValues.maxQuantity === undefined ||
                      currentFormValues.minQuantity === null ||
                      currentFormValues.maxQuantity === null ||
                      +currentFormValues?.maxQuantity <
                        +currentFormValues?.minQuantity ||
                      // @ts-ignore - Form inputs return strings, not numbers
                      currentFormValues.minQuantity === "" ||
                      // @ts-ignore - Form inputs return strings, not numbers
                      currentFormValues.maxQuantity === ""
                    }
                    loading={isVariantSaving}
                  >
                    {isVariantSaving ? "Saving..." : "Save Variant"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);

// Main ProductCard2 component
const ProductCard2: React.FC<ProductCard2Props> = ({
  currencyCode,
  product,
  expandedProducts,
  expandedVariants,
  productConfigs,
  updateProductConfig,
  isProductConfigValid,
  handleSaveProduct,
  toggleProductExpansion,
  toggleVariantExpansion,
  isUpdatingProduct,
  updatingProductId, // Add this new prop
  productDetailsPage,
  selectedVariants,
  variations,
  showOnlyCurrentlyAssignedVariants,
}) => {
  const isExpanded = expandedProducts.has(product?.id);
  const config = productConfigs[product?.id] || product;
  // const navigate = useNavigate();
  const { id: tenantId } = useParams();
  const queryClient = useQueryClient();
  // React Hook Form setup
  const {
    register,
    formState: { isDirty, errors },
    watch,
    getValues,
    reset,
  } = useForm({
    defaultValues: {
      price: config.price ?? null,
      minQuantity: product.minimum_quantity ?? product.minQuantity,
      maxQuantity: product.maximum_quantity ?? product.maxQuantity,
      customerProductId: config.customerProductId || "",
      customerProductName: config.customerProductName || "",
    },
    mode: "onChange",
  });

  // Watch current form values for parent product
  const currentFormValues = watch();

  // Use the custom hook for parent product customer view
  const {
    customerView: parentCustomerView,
    toggleCustomerView: toggleParentCustomerView,
    isDisabled: isParentCustomerViewDisabled,
    isLoading: isParentCustomerViewLoading,
  } = useCustomerView({
    initialCustomerView: product.customerView || false,
    productId: product.tenantProductId || product?.id,
    isDisabled:
      !config.price ||
      config.minQuantity === null ||
      config.maxQuantity === null,
    tenantId: tenantId || "",
    parentProductId: product?.tenantProductId || product?.id,
  });
  // Fetch variants for this product if variations prop is not provided
  const { data: productVariationsResponse, isLoading: isLoadingVariations } =
    useQuery({
      ...productQueries.queries.productVariations(
        product.secondId || "",
        tenantId || ""
      ),
      // enabled:
      // !!product.secondId && isExpanded && product.isParent && !variations,
    });

  // Handle parent customer view toggle with variations invalidation
  const handleParentCustomerViewToggle = () => {
    toggleParentCustomerView();
  };

  // Check if this specific product is being updated
  const isProductSaving =
    isUpdatingProduct && updatingProductId === product.tenantProductId;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div
        className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
        onClick={() => toggleProductExpansion(product?.id)}
      >
        <div className="flex items-center gap-3">
          <img
            src={product.image ?? ProductIcon}
            alt="product"
            className="rounded-full w-[40px] h-[40px]"
          />
          <div>
            <h3 className="text-gray-900 font-medium">{product.name}</h3>
            <p className="text-sm text-gray-500">
              {product.isParent ? "Parent Product" : "Variant"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!productDetailsPage && (
            <Badge
              variant={isParentCustomerViewDisabled ? "default" : "active"}
              className="!rounded-lg"
            >
              {isParentCustomerViewDisabled ? "Draft" : "Completed"}
            </Badge>
          )}

          {productDetailsPage && (
            <Badge
              variant={!parentCustomerView ? "default" : "active"}
              className="!rounded-lg"
            >
              {!parentCustomerView ? "Draft" : "Published"}
            </Badge>
          )}
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-6 py-4 border-t border-gray-200 space-y-4">
          {/* Customer Product Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer product name
            </label>
            <div className="relative">
              <input
                {...register("customerProductName", {
                  // required: "Customer product name is required",
                  pattern: {
                    value: /^[a-zA-Z\s]+$/,
                    message:
                      "Customer product name can only contain letters and spaces",
                  },
                  minLength: {
                    value: 5,
                    message:
                      "Customer product name must be at least 5 characters",
                  },
                })}
                placeholder="Customer product name"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none ${
                  errors.customerProductName
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              />
              {errors.customerProductName && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.customerProductName.message}
                </p>
              )}
              <div className="absolute right-3 top-2.5">
                <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center">
                  <span className="text-xs text-gray-600">?</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              This name will appear in the customer's catalog.
            </p>
          </div>

          {/* Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Price <span className="text-primary-blue">*</span>
            </label>
            <div className="relative">
              <input
                {...register("price", {
                  required: "Price is required",
                  min: { value: 0, message: "Price must be positive" },
                })}
                type="number"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
              />
              <div className="absolute right-3 top-2.5">
                <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center">
                  <span className="text-xs text-gray-600">?</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              This is the recommended price after calculating the user account
              discount.
            </p>
          </div>

          {/* Quantity Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Quantity <span className="text-primary-blue">*</span>
              </label>
              <input
                {...register("minQuantity", {
                  required: "Minimum quantity is required",
                  min: {
                    value: 1,
                    message: "Minimum quantity must be at least 1",
                  },
                })}
                type="number"
                placeholder="Minimum Quantity"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maximum Quantity <span className="text-primary-blue">*</span>
              </label>
              <input
                {...register("maxQuantity", {
                  required: "Maximum quantity is required",
                  min: {
                    value: 1,
                    message: "Maximum quantity must be at least 1",
                  },
                  max: {
                    value: 999,
                    message: "Maximum quantity cannot exceed 999",
                  },
                })}
                type="number"
                placeholder="Maximum Quantity"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
              />
              {errors.maxQuantity?.message ==
                "Maximum quantity cannot exceed 999" && (
                <p className="text-red-500 text-xs ">
                  {errors.maxQuantity?.message}
                </p>
              )}
            </div>
          </div>

          {/* Customer Product ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer - Product ID
            </label>
            <div className="relative">
              <input
                {...register("customerProductId", {
                  pattern: {
                    value: /^[a-zA-Z0-9]+$/,
                    message:
                      "Customer Product ID can only contain letters and numbers",
                  },
                  minLength: {
                    value: 2,
                    message:
                      "Customer Product ID must be at least 2 characters",
                  },
                  maxLength: {
                    value: 50,
                    message:
                      "Customer Product ID must be less than 50 characters",
                  },
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
              />
              {errors.customerProductId && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.customerProductId.message}
                </p>
              )}
              <div className="absolute right-3 top-2.5">
                <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center">
                  <span className="text-xs text-gray-600">?</span>
                </div>
              </div>
            </div>
          </div>

          {/* Save Button for Parent Product */}
          <div className="flex justify-between pt-4">
            {/* Customer View Toggle */}
            <div
              className={`flex items-center gap-3 cursor-pointer ${
                isParentCustomerViewDisabled
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              onClick={handleParentCustomerViewToggle}
            >
              <img
                src={parentCustomerView ? VisibleIcon : InvisibleIcon}
                alt="visibility toggle"
                className="w-11 h-6"
              />
              <span className="text-sm font-medium text-gray-700">
                {isParentCustomerViewLoading ? "Updating..." : "Visible"}
              </span>
            </div>

            {/* Save Button */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // Reset form to initial values
                  reset();
                }}
                disabled={isProductSaving || !isDirty} // Disable when saving or no changes
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={async () => {
                  const formData = {
                    price: getValues().price || null,
                    minQuantity: getValues().minQuantity || null,
                    maxQuantity: getValues().maxQuantity || null,
                    customerProductId: getValues().customerProductId || "",
                    customerProductName: getValues().customerProductName || "",
                  };

                  try {
                    await handleSaveProduct(
                      product?.id,
                      product?.tenantProductId,
                      formData
                    );

                    // Update productConfigs with new values after successful save
                    updateProductConfig(product?.id, "price", formData.price);
                    updateProductConfig(
                      product?.id,
                      "minQuantity",
                      formData.minQuantity
                    );
                    updateProductConfig(
                      product?.id,
                      "maxQuantity",
                      formData.maxQuantity
                    );
                    updateProductConfig(
                      product?.id,
                      "customerProductId",
                      formData.customerProductId
                    );
                    updateProductConfig(
                      product?.id,
                      "customerProductName",
                      formData.customerProductName
                    );

                    // Mark as having initial values
                    updateProductConfig(product?.id, "hasInitialValues", true);
                    updateProductConfig(product?.id, "hasChanges", false);

                    // Reset form with the saved values as new defaults
                    reset(formData);
                  } catch (error) {
                    console.error("Error saving product:", error);
                  }

                  // Update the query cache directly with new data
                  queryClient.setQueryData(
                    [
                      "admin",
                      "product",
                      "variations",
                      product.secondId || "",
                      product.tenantProductId || "",
                    ],
                    (oldData: any) => {
                      if (!oldData) return oldData;
                      return oldData.map((item: any) => {
                        if (item.product_id.toString() === product.secondId) {
                          return {
                            ...item,
                            tenant_product_prices: [
                              { price: formData.price, currency: currencyCode! },
                            ],
                            minimum_quantity: formData.minQuantity,
                            maximum_quantity: formData.maxQuantity,
                            tenant_product_sku: formData.customerProductId,
                          };
                        }
                        return item;
                      });
                    }
                  );
                }}
                disabled={(() => {
                  const isDisabled = !isDirty || Object.keys(errors).length > 0;
                  const isInInvalid =
                    // @ts-ignore - Form inputs return strings, not numbers
                    getValues().minQuantity === "" ||
                    // @ts-ignore - Form inputs return strings, not numbers
                    getValues().maxQuantity === "" ||
                    // @ts-ignore - Form inputs return strings, not numbers
                    currentFormValues.minQuantity === "" ||
                    // @ts-ignore - Form inputs return strings, not numbers
                    currentFormValues.maxQuantity === "" ||
                    // @ts-ignore - Form inputs return strings, not numbers
                    (!!currentFormValues?.minQuantity &&
                      !!currentFormValues?.maxQuantity &&
                      Number(currentFormValues.minQuantity) >
                        Number(currentFormValues.maxQuantity));

                  return isDisabled || isInInvalid;
                })()}
                loading={isProductSaving}
              >
                {isProductSaving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>

          {/* Variants Section */}
          {product.isParent &&
            (variations || productVariationsResponse)?.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Product Variants
                </h4>
                <div className="space-y-3">
                  {isLoadingVariations ? (
                    <div className="text-sm text-gray-500 italic">
                      Loading variants...
                    </div>
                  ) : (
                    (variations || productVariationsResponse)
                      .filter(
                        (variant: any) =>
                          // Skip the parent product itself
                          variant?.catalog_info?.id?.toString() !== product?.id
                      )
                      .map((variant: any) => {
                        // Format feature values for display
                        const featureDisplay =
                          variant?.catalog_info?.feature_values
                            ?.map(
                              (feature: any) =>
                                feature.featureVariant.variant_name
                            )
                            .join(", ") || "";

                        const variantData = {
                          id: variant.id.toString(),
                          name: featureDisplay,
                          price:
                            variant.tenant_product_prices[0]?.price ||
                            variant.catalog_info?.price ||
                            null,
                          minQuantity: variant.minimum_quantity || null,
                          maxQuantity: variant.maximum_quantity || null,
                          customerProductId: variant.tenant_product_sku || "",
                          isParent: false,
                          tenantProductId: variant.id.toString(),
                          productId: variant.catalog_info?.id,
                          isVisible: variant.currently_assigned || false,
                          tenantInfoId: variant.id.toString(),
                          hasInitialValues: false,
                          hasChanges: false,
                          customer_view: variant.customer_view,
                          price_assigned: variant.price_assigned,
                          minimum_quantity: variant.minimum_quantity,
                          maximum_quantity: variant.maximum_quantity,
                          tenant_product_sku: variant.tenant_product_sku,
                          image:
                            variant?.catalog_info?.product_images?.[0]
                              ?.image_path,
                        };

                        // Remove the selectedVariants filtering - show all variants
                        if (
                          selectedVariants &&
                          !selectedVariants.has(variantData.id)
                        ) {
                          return null;
                        }

                        return (
                          <VariantCard
                            key={`${variant.id}-${variant.tenant_product_prices?.[0]?.price}-${variant.minQuantity}-${variant.maxQuantity}`}
                            tenantId={tenantId || ""}
                            variant={variantData}
                            product={productVariationsResponse?.[0]}
                            productConfigs={productConfigs}
                            updateProductConfig={updateProductConfig}
                            isProductConfigValid={isProductConfigValid}
                            handleSaveProduct={handleSaveProduct}
                            toggleVariantExpansion={toggleVariantExpansion}
                            isUpdatingProduct={isUpdatingProduct}
                            updatingProductId={updatingProductId} // Pass the updatingProductId
                            expandedVariants={expandedVariants}
                            productDetailsPage={productDetailsPage}
                            showOnlyCurrentlyAssignedVariants={
                              showOnlyCurrentlyAssignedVariants
                            }
                          />
                        );
                      })
                      .filter(Boolean) // Remove null entries
                  )}
                </div>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default ProductCard2;
